import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { useLang } from "@/i18n/LangContext";
import { Menu, X, Droplets } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = ({ transparent = false }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [complianceEnabled, setComplianceEnabled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then((res) => setComplianceEnabled(!!res.data?.compliance_enabled))
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ios-safe-top ${
        transparent ? "bg-transparent" : "bg-[#050505]/90 backdrop-blur-xl border-b border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <Droplets className="w-6 h-6 text-purple-500 group-hover:text-pink-500 transition-colors duration-300" />
          <span
            className="text-lg font-bold tracking-[0.15em] uppercase neon-text-purple"
            style={{ fontFamily: "'Cinzel Decorative', serif" }}
          >
            P INK
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <a href="/#gallery" className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 tracking-wider uppercase" data-testid="nav-gallery">{t("nav_gallery")}</a>
          <a href="/#about" className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 tracking-wider uppercase" data-testid="nav-about">{t("nav_about")}</a>
          <a href="/#reviews" className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 tracking-wider uppercase" data-testid="nav-reviews">{t("nav_reviews")}</a>
          <a href="/#contact" className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 tracking-wider uppercase" data-testid="nav-contact">{t("nav_contact")}</a>
          {complianceEnabled && (
            <Link to="/compliance" className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 tracking-wider uppercase" data-testid="nav-compliance">Compliance</Link>
          )}
          {user ? (
            <div className="flex items-center gap-4">
              {user.is_admin ? (
                <Link to="/admin" className="text-sm text-purple-400 hover:text-purple-300 transition-colors duration-200 tracking-wider uppercase" data-testid="nav-admin">{t("nav_admin")}</Link>
              ) : (
                <Link to="/dashboard" className="text-sm text-purple-400 hover:text-purple-300 transition-colors duration-200 tracking-wider uppercase" data-testid="nav-dashboard">{t("tab_dashboard")}</Link>
              )}
              <button onClick={handleLogout} className="text-sm text-neutral-500 hover:text-white transition-colors duration-200 tracking-wider uppercase" data-testid="nav-logout">{t("nav_logout")}</button>
            </div>
          ) : (
            <Link to="/login" className="bg-purple-600 hover:bg-purple-500 text-white font-bold py-2 px-6 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-xs" data-testid="nav-login">{t("nav_client_login")}</Link>
          )}
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden text-neutral-400 hover:text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/5 px-6 py-6 space-y-4" data-testid="nav-mobile-menu">
          <a href="/#gallery" onClick={() => setMobileOpen(false)} className="block text-sm text-neutral-400 hover:text-white tracking-wider uppercase">{t("nav_gallery")}</a>
          <a href="/#about" onClick={() => setMobileOpen(false)} className="block text-sm text-neutral-400 hover:text-white tracking-wider uppercase">{t("nav_about")}</a>
          <a href="/#reviews" onClick={() => setMobileOpen(false)} className="block text-sm text-neutral-400 hover:text-white tracking-wider uppercase">{t("nav_reviews")}</a>
          <a href="/#contact" onClick={() => setMobileOpen(false)} className="block text-sm text-neutral-400 hover:text-white tracking-wider uppercase">{t("nav_contact")}</a>
          {complianceEnabled && (
            <Link to="/compliance" onClick={() => setMobileOpen(false)} className="block text-sm text-neutral-400 hover:text-white tracking-wider uppercase">Compliance</Link>
          )}
          {user ? (
            <>
              {user.is_admin ? (
                <Link to="/admin" onClick={() => setMobileOpen(false)} className="block text-sm text-purple-400 tracking-wider uppercase">{t("nav_admin")}</Link>
              ) : (
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block text-sm text-purple-400 tracking-wider uppercase">{t("tab_dashboard")}</Link>
              )}
              <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block text-sm text-neutral-500 tracking-wider uppercase">{t("nav_logout")}</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-sm text-purple-400 tracking-wider uppercase">{t("nav_client_login")}</Link>
          )}
        </div>
      )}
    </nav>
  );
};
